/*
 * Copyright (c) 2025 CalAmp Corp.  All Rights Reserved
 */
import React, {useEffect, useRef, useState} from 'react';
import {makeStyles, MenuList} from '@material-ui/core';
import {translate} from '@ion/core/src/locale/locale';
import {CellMenuItem} from '../../components/grid/CellMenuItem';
import {MapToolbarVicinitySearchIcon} from '../../icons/current/VicinitySearchIcon';
import {dlsColor} from '../../theme/theme';
import useThemeContext from '../../theme/useThemeContext';
import {IconSize} from '../../types/types';

/**
 * Defines the default layout constants used by the `MapContextMenu`
 * for consistent positioning and overflow handling.
 */
export const MENU_CONSTANTS = {
  BASE_OFFSET_X  : 20,
  BASE_OFFSET_Y  : -72,
  BELOW_OFFSET_Y : 24,
  PADDING        : 12,
  CONTENT_W      : 192,
  CONTENT_H      : 76,
  INITIAL_STATE  : {left: -300, top: -300} as {left: number; top: number}
};

// the current theme's menu - its title, a single 44px option and its border
// - for keeping it within the map as the legacy menu's size is ...
const CURRENT_CONTENT_H = 78;

/**
 * Props for the `MapContextMenu` component.
 */
export interface MapContextMenuProps {
  x?: number;
  y?: number;
  visible?: boolean;
  onClose?: () => void;
  onVicinitySearch?: () => void;
  view?: __esri.MapView | null;
};

const useStyle = makeStyles(() => ({
  root : {
    position      : 'fixed',
    zIndex        : 9999,
    background    : dlsColor('white'),
    border        : '1px solid #ccc',
    borderRadius  : 8,
    padding       : '10px 12px',
    boxShadow     : '0 4px 12px rgba(0,0,0,0.2)',
    pointerEvents : 'auto',
    minWidth      : 128,
    transition    : 'opacity 0.1s ease-in-out'
  },
  title : {
    fontWeight   : 600,
    marginBottom : 8,
    fontSize     : 14
  },
  option : {
    display    : 'block',
    width      : '100%',
    textAlign  : 'left',
    background : 'none',
    border     : 'none',
    cursor     : 'pointer',
    padding    : '6px 0',
    fontSize   : 14
  },
  // the current theme's menu, as its other options menus are (e.g. a
  // favorite filter's) - a white, grey bordered, rounded card titled above
  // its 44px rows, each an icon and a label ...
  currentRoot : {
    position          : 'fixed',
    zIndex            : 9999,
    boxSizing         : 'border-box',
    // only as wide as its widest row, the same inset both sides ...
    width             : 'max-content',
    overflow          : 'hidden',
    background        : dlsColor('white'),
    border            : `1px solid ${dlsColor('grey5')}`,
    borderRadius      : 12,
    filter            : 'drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.25))',
    pointerEvents     : 'auto',
    '& .MuiList-root' : {
      padding : 0
    },
    // each item's own content box adds 8px, for the 16px inset ...
    '& .MuiMenuItem-root' : {
      boxSizing  : 'border-box',
      height     : 44,
      padding    : '8px',
      width      : '100%',
      fontFamily : 'Inter',
      fontWeight : 500,
      fontSize   : 14,
      lineHeight : '20px',
      color      : dlsColor('grey1')
    }
  },
  currentTitle : {
    padding       : '12px 16px 4px',
    fontFamily    : 'Roboto',
    fontWeight    : 700,
    fontSize      : 12,
    lineHeight    : '16px',
    letterSpacing : '1px',
    textTransform : 'uppercase',
    color         : dlsColor('grey2')
  }
}));

/**
 * Dynamically positions itself near the user's click on the map,
 * ensuring that it stays visible within the viewport.
 *
 * ✨ Features:
 * - Default position: 25px right, 110px above the click point  
 * - Automatically adjusts to avoid screen overflow  
 * - Closes on outside click or Escape key  
 * - Triggers optional callbacks for custom actions  
 *
 * ```tsx
 * import { MapContextMenu } from './MapContextMenu';
 *
 * <MapContextMenu x={clickX}
 *   y={clickY}
 *   visible={menuVisible}
 *   onVicinitySearch={() => console.log('Vicinity Search')}
 *   onClose={() => setMenuVisible(false)}
 * />
 * ```
 */
export const MapContextMenu = ({
  view,
  x,
  y,
  visible,
  onClose,
  onVicinitySearch
}: MapContextMenuProps) => {
  const classes            = useStyle();
  const {isCurrent}        = useThemeContext();
  const menuRef            = useRef<HTMLDivElement>(null);
  const [position, setPos] = useState(MENU_CONSTANTS.INITIAL_STATE);

  useEffect(() => {
    const {
      CONTENT_W,
      PADDING,
      BASE_OFFSET_X,
      BASE_OFFSET_Y,
      BELOW_OFFSET_Y,
      INITIAL_STATE
    } = MENU_CONSTANTS;
    const contentHeight = isCurrent ? CURRENT_CONTENT_H : MENU_CONSTANTS.CONTENT_H;

    if (!visible || x == null || y == null) {
      setPos(INITIAL_STATE);
      return;
    }

    const root = (view?.container ?? document.querySelector<HTMLElement>('.esri-view-root')) as HTMLElement;
    if (!root) {
      setPos(INITIAL_STATE);
      return;
    }

    const {width: vw, height: vh, left: rootLeft, top: rootTop} = root.getBoundingClientRect();

    let left = x + BASE_OFFSET_X;
    let top = y + BASE_OFFSET_Y;

    if (left + CONTENT_W > rootLeft + vw - PADDING) {
      left = x - CONTENT_W - BASE_OFFSET_X;
    }

    if (top < rootTop + PADDING) {
      top = y + BELOW_OFFSET_Y;
    } else if (top + contentHeight > rootTop + vh - PADDING) {
      const altTop = y - contentHeight - BELOW_OFFSET_Y;
      top = altTop >= rootTop + PADDING ? altTop : rootTop + vh - contentHeight - PADDING;
    }

    setPos({left, top});
  }, [x, y, visible, isCurrent]);
  
  function onContextMenu(e) {
    e.preventDefault();
  }

  function onVicinityClick() {
    onVicinitySearch?.();
    onClose?.();
  }

  if (!visible || !x || !y) return null;

  if (isCurrent) {
    return (
      <div onContextMenu={onContextMenu}
        data-testid='map-context-menu'
        className={classes.currentRoot}
        ref={menuRef}
        style={{
          left : position.left,
          top  : position.top
        }}>
        <div className={classes.currentTitle}>
          {translate('maps:contextMenu.title')}
        </div>
        <MenuList autoFocusItem>
          <CellMenuItem icon={<MapToolbarVicinitySearchIcon size={IconSize.medium} color={dlsColor('grey1')} />}
            label={translate('maps:contextMenu.vicinitySearch')}
            onClick={onVicinityClick} />
        </MenuList>
      </div>
    );
  }

  return (
    <div onContextMenu={onContextMenu}
      data-testid='map-context-menu'
      className={classes.root}
      ref={menuRef}
      style={{
        left : position.left,
        top  : position.top
      }}>
      <div className={classes.title}>
        {translate('maps:contextMenu.title')}
      </div>

      <button className={classes.option}
        onClick={onVicinityClick}>
        {translate('maps:contextMenu.vicinitySearch')}
      </button>
    </div>
  );
};

export default MapContextMenu;
